'use client';

/**
 * VoxChatDock - chat panel vpravo dole (vzor Homes.com, nas styl).
 * Otvara ho expand button v hero inpute cez custom event
 * (ziadny cross-feature import features/adverts -> features/vox).
 *
 * Zatial UI shell s demo odpovedou - realny dialog dodá VOX engine
 * (Kiso, branch realico-vox). Napojit: nahradit sendDemo() enginom.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVoxCtx } from '../lib/VoxProvider';
import TextareaAutosize from 'react-textarea-autosize';
import { cn } from '@/shadcn/lib/utils';
import { MOTION, LIFT } from '@/shared/components/ui/motion';
import { ArrowDown } from 'lucide-react';
import { Minimize2 } from 'lucide-react';
import { VoxMicLauncher } from './VoxMicLauncher';

/** Event, ktorym hero input otvara dock. */
export const VOX_CHAT_OPEN_EVENT = 'rx-vox-chat-open';

type Msg = { role: 'ai' | 'user'; text: string };

/** Meno AI asistentky - pouzije sa aj pre hlas. Predstavi sa v prvej sprave. */
export const AI_NAME = 'Realica';

const GREETING: Msg = {
    role: 'ai',
    text: `Ahoj, som ${AI_NAME}. Povedz alebo napíš, čo hľadáš - nájdeme to spolu.`,
};

const DEMO_REPLY =
    'Rozumiem! Vyhľadávanie práve ladíme - toto je náhľad rozhrania.';

/** Rychle navrhy - vyplnia prazdny stav a navedu uzivatela. */
const SUGGESTIONS = [
    '2-izbový v Ružinove',
    'Dom so záhradou pri BA',
    'Investičný byt do 200 000 €',
    'Novostavba 3-izbový',
];

/* Realica - portret na zltom pozadi (DZ 27.8.) v kruhu so zlatym ringom.
   Asset je uz stvorcovy vyrez hlavy 160x160 (public/images/vox_avatar.png), staci cover + center. */
function AiAvatar() {
    return (
        <span
            aria-hidden
            className={cn(
                'relative mt-[1px] flex size-[26px] shrink-0 overflow-hidden rounded-full',
                // bez ringu: pozadie fotky je brand zlta #F3C300 a tá sa voci bielemu panelu
                // oddeli sama - akykolvek ring tu len pridava linku navyse
                'bg-[#F3C300]',
            )}
        >
            <span
                aria-hidden
                className="absolute inset-0 bg-no-repeat"
                style={{
                    backgroundImage: 'url(/images/vox_avatar.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            />
        </span>
    );
}

export function VoxChatDock() {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const replyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Skutocny engine, ked je stranka obalena VoxProviderom. Bez neho ostava
    // povodne demo spravanie - dok sa da pouzit aj samostatne.
    const vox = useVoxCtx();
    const [localMessages, setLocalMessages] = useState<Msg[]>([GREETING]);
    const [localThinking, setLocalThinking] = useState(false);

    // useMemo, aby sa pole nevytvaralo pri kazdom prekresleni - inak by efekt
    // na odrolovanie na spodok bezal donekonecna.
    const messages: Msg[] = useMemo(
        () =>
            vox
                ? vox.messages.length
                    ? vox.messages.map((m) => ({ role: m.role === 'user' ? 'user' : 'ai', text: m.text } as Msg))
                    : [GREETING]
                : localMessages,
        [vox, localMessages],
    );
    const thinking = vox ? vox.phase === 'thinking' : localThinking;

    function pushUserAndReply(text: string) {
        if (vox) {
            vox.sendText(text);
            return;
        }
        setLocalMessages((m) => [...m, { role: 'user', text }]);
        setLocalThinking(true);
        replyTimer.current = setTimeout(() => {
            setLocalThinking(false);
            setLocalMessages((m) => [...m, { role: 'ai', text: DEMO_REPLY }]);
        }, 750);
    }

    useEffect(() => {
        const openHandler = (e: Event) => {
            setOpen(true);
            const text = (e as CustomEvent<{ text?: string }>).detail?.text?.trim();
            if (text) pushUserAndReply(text);
        };
        // Pozn.: hlasovy start posle tu istu udalost bez textu - dok sa len otvori.
        window.addEventListener(VOX_CHAT_OPEN_EVENT, openHandler);
        return () => {
            window.removeEventListener(VOX_CHAT_OPEN_EVENT, openHandler);
            if (replyTimer.current) clearTimeout(replyTimer.current);
        };
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages, open, thinking]);


    function sendText(raw: string) {
        const text = raw.trim();
        if (!text) return;
        setValue('');
        pushUserAndReply(text);
    }

    const showSuggestions = messages.length === 1 && !thinking;
    const canSend = value.trim().length > 0;

    return (
        <div
            className={cn(
                'fixed z-[1100] right-[12px] bottom-[12px] md:right-[24px] md:bottom-[24px]',
                'w-[calc(100vw-24px)] max-w-[384px]',
                'transition duration-[380ms] ease-[var(--ease-rx)]',
                open
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-[16px] pointer-events-none',
            )}
            aria-hidden={!open}
        >
            <div
                className={cn(
                    open && 'rxc-panel-in',
                    'relative overflow-hidden rounded-[24px]',
                    'bg-white/85 backdrop-blur-[22px] backdrop-saturate-[1.4]',
                    'shadow-[0_30px_80px_-24px_rgba(60,42,0,0.4),0_2px_10px_rgba(0,0,0,0.06)]',
                    'ring-1 ring-black/[.06]',
                )}
            >
                {/* zlata linka - nas podpis */}
                <div
                    aria-hidden
                    className="h-[3px] bg-[linear-gradient(90deg,#c98f00_0%,#f3c300_28%,#fff2c0_50%,#f3c300_72%,#c98f00_100%)]"
                />

                {/* header */}
                <div className="flex items-center justify-between px-[16px] pt-[13px] pb-[10px]">
                    <div className="flex items-center gap-[9px]">
                        <AiAvatar />
                        <div className="leading-tight">
                            <p className="text-[13.5px] font-[700] tracking-[-0.01em] text-black/90">
                                {AI_NAME}
                            </p>
                            <span className="flex items-center gap-[5px] text-[10.5px] font-[500] text-black/45">
                                <span className="inline-block size-[6px] rounded-full bg-[#22c55e] shadow-[0_0_0_3px_rgba(34,197,94,.16)]" />
                                Online · asistentka Realico
                            </span>
                        </div>
                    </div>
                    <button
                        aria-label="Zbaliť chat"
                        title="Zbaliť"
                        className={cn(
                            'size-[30px] flex items-center justify-center rounded-full cursor-pointer',
                            'text-black/50 hover:text-black hover:bg-black/[.05]',
                            LIFT,
                            MOTION,
                        )}
                        onClick={() => setOpen(false)}
                    >
                        <Minimize2 className="size-[15px]" />
                    </button>
                </div>

                {/* jemna delici linka pod headerom */}
                <div
                    aria-hidden
                    className="mx-[16px] h-px bg-[linear-gradient(90deg,transparent,rgba(0,0,0,.08)_18%,rgba(0,0,0,.08)_82%,transparent)]"
                />

                {/* spravy */}
                <div className="relative">
                    {/* ambient zlaty nadych za spravami */}
                    <div aria-hidden className="rxc-ambient pointer-events-none absolute inset-0" />
                    {/* fade masky pre elegantny scroll */}
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-[16px] bg-[linear-gradient(rgba(255,255,255,.9),transparent)]"
                    />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-[16px] bg-[linear-gradient(transparent,rgba(255,255,255,.9))]"
                    />

                    <div
                        ref={scrollRef}
                        className="relative z-[1] h-[312px] overflow-y-auto vox-scroll-light px-[16px] py-[14px] flex flex-col gap-[12px]"
                    >
                        {messages.map((m, i) =>
                            m.role === 'ai' ? (
                                <div key={i} className="rxc-msg flex items-start gap-[8px] max-w-[94%]">
                                    <AiAvatar />
                                    <div
                                        className={cn(
                                            'rounded-[16px] rounded-tl-[6px] px-[13px] py-[9px]',
                                            'bg-white/90 ring-1 ring-black/[.06]',
                                            'shadow-[0_6px_18px_-10px_rgba(0,0,0,.28)]',
                                        )}
                                    >
                                        <p className="text-[13.5px] leading-[150%] text-black/85">
                                            {m.text}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    key={i}
                                    className={cn(
                                        'rxc-msg self-end max-w-[85%] rounded-[16px] rounded-br-[6px]',
                                        'px-[13px] py-[9px] text-black/85',
                                        'bg-[linear-gradient(135deg,#fff4d1_0%,#ffe9a8_100%)]',
                                        'ring-1 ring-[#f3c300]/25',
                                        'shadow-[0_6px_18px_-10px_rgba(201,143,0,.45)]',
                                    )}
                                >
                                    <p className="text-[13.5px] leading-[150%]">{m.text}</p>
                                </div>
                            ),
                        )}

                        {/* typing indikator */}
                        {thinking && (
                            <div className="rxc-msg flex items-start gap-[8px]">
                                <AiAvatar />
                                <div className="flex items-center gap-[4px] rounded-[16px] rounded-tl-[6px] bg-white/90 px-[14px] py-[12px] ring-1 ring-black/[.06]">
                                    {[0, 1, 2].map((d) => (
                                        <span
                                            key={d}
                                            className="rxc-dot size-[6px] rounded-full bg-[#c98f00]"
                                            style={{ animationDelay: `${d * 0.15}s` }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* navrhy - len v prazdnom stave */}
                        {showSuggestions && (
                            <div className="mt-[2px] flex flex-wrap gap-[7px] pl-[34px]">
                                {SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => sendText(s)}
                                        style={{ animationDelay: `${120 + i * 70}ms` }}
                                        className={cn(
                                            'rxc-chip cursor-pointer rounded-full px-[12px] py-[6px]',
                                            'text-[12px] font-[500] text-black/70 hover:text-black/90',
                                        )}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* vstup */}
                <div className="px-[12px] pb-[12px] pt-[8px]">
                    <div className="flex items-end gap-[9px]">
                        <div className="flex-1 min-w-0 relative">
                            <TextareaAutosize
                                ref={inputRef}
                                minRows={1}
                                maxRows={4}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendText(value);
                                    }
                                }}
                                placeholder="Napíš, čo hľadáš…"
                                className={cn(
                                    'block w-full resize-none rounded-[20px] border border-black/10',
                                    'bg-white/70 px-[15px] py-[10px] pr-[44px] text-[13.5px] text-black/85',
                                    // ziadny viditelny scrollbar - pri dlhom texte roluje potichu za caretom
                                    '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                                    'placeholder:text-black/40',
                                    'shadow-[inset_0_1px_2px_rgba(0,0,0,.04)]',
                                    'focus-visible:outline-none focus-visible:border-[#f3c300]/70',
                                    'focus-visible:ring-[3px] focus-visible:ring-[#f3c300]/18 focus-visible:bg-white',
                                    'transition duration-200 ease-[var(--ease-rx)]',
                                )}
                            />
                            <button
                                aria-label="Odoslať"
                                disabled={!canSend}
                                className={cn(
                                    'absolute right-[6px] bottom-[5px]',
                                    'size-[30px] flex items-center justify-center rounded-full',
                                    'transition duration-[200ms] ease-[var(--ease-rx)]',
                                    // rovnaka sipka ako vo velkom hero inpute: transparentna, zlty hover
                                    canSend
                                        ? cn('cursor-pointer bg-transparent text-black hover:bg-[#f8e5b0]', LIFT)
                                        : 'cursor-not-allowed bg-transparent text-black/25',
                                )}
                                onClick={() => sendText(value)}
                            >
                                {/* lucide ArrowDown otocena doprava - identicka so sipkou v hero inpute */}
                                <ArrowDown className="size-[16px] -rotate-90" />
                            </button>
                        </div>
                        {/* ten isty soundwave launcher ako v hero inpute, len fixnych 38px */}
                        <VoxMicLauncher variant="chat" />
                    </div>
                    <p className="mt-[7px] text-center text-[10px] text-black/35">
                        {AI_NAME} je v náhľade · odpovede sa ešte ladia
                    </p>
                </div>
            </div>
        </div>
    );
}
